<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { useDemoChat, type Message } from '@/composables/useDemoChat';
import DemoChatPanel from './DemoChatPanel.vue';

type LlmStyle = 'chatgpt' | 'claude' | 'claude-code';

const props = withDefaults(defineProps<{
  llmStyle?: LlmStyle;
}>(), {
  llmStyle: 'chatgpt',
});

interface Deck {
  id: string;
  name: string;
  cardCount: number;
  toDelete?: boolean;
  toRename?: boolean;
}

// Demo script
const conversation: Message[] = [
  { role: 'user', content: "I have two SR decks and I'm getting confused. Can you delete the strontium one and rename the other to just Spaced Repetition?" },
  { role: 'assistant', content: "Sure! I'll delete SR (Strontium) and rename the other deck to Spaced Repetition." },
];

const initialDecks: Deck[] = [
  { id: '1', name: 'SR (Spaced Repetition)', cardCount: 5, toRename: true },
  { id: '2', name: 'SR (Strontium)', cardCount: 3, toDelete: true },
];

const afterUpdateDecks: Deck[] = [
  { id: '1', name: 'Spaced Repetition', cardCount: 5 },
];

const {
  visibleMessages, isTyping, typingContent, typingRole, demoVisible,
  sleep, showUserMessage, showAssistantMessage, reset, fadeIn, cleanup,
  MESSAGE_DELAY,
} = useDemoChat();

// Demo-specific state
const currentDecks = ref<Deck[]>(initialDecks.map(d => ({ ...d })));
const isUpdating = ref(false);
const UPDATE_DELAY = 600;

async function runDemo() {
  reset();
  currentDecks.value = initialDecks.map(d => ({ ...d }));
  isUpdating.value = false;

  await fadeIn();

  await showUserMessage(conversation[0]);
  await sleep(MESSAGE_DELAY);
  await showAssistantMessage(conversation[1]);

  // Show updating state then apply changes
  await sleep(UPDATE_DELAY);
  isUpdating.value = true;
  await sleep(1200);
  currentDecks.value = afterUpdateDecks.map(d => ({ ...d }));
  isUpdating.value = false;
}

onMounted(() => runDemo());
onUnmounted(() => cleanup());
</script>

<template>
  <Transition name="demo">
    <div v-show="demoVisible" class="grid md:grid-cols-2 gap-6">
    <!-- Left: Chat -->
    <DemoChatPanel
      :llm-style="props.llmStyle"
      :visible-messages="visibleMessages"
      :is-typing="isTyping"
      :typing-content="typingContent"
      :typing-role="typingRole"
    />

    <!-- Right: App (Deck View) -->
    <div>
      <h2 class="text-xl font-semibold text-foreground mb-3">Your flashcards in LLM-SR</h2>
      <div class="rounded-lg border border-border bg-card overflow-hidden shadow-md">
      <!-- Header bar -->
      <div class="px-3 py-2 border-b border-border bg-muted/50 flex items-center gap-2">
        <div class="flex gap-1.5">
          <div class="w-2.5 h-2.5 rounded-full bg-red-400/70"></div>
          <div class="w-2.5 h-2.5 rounded-full bg-yellow-400/70"></div>
          <div class="w-2.5 h-2.5 rounded-full bg-green-400/70"></div>
        </div>
      </div>

      <!-- App content - Deck list -->
      <div class="p-4 h-[220px] md:h-[320px] overflow-hidden">
        <div class="flex items-center justify-between mb-3">
          <h3 class="font-medium text-foreground text-sm">Your Decks</h3>
          <span class="text-xs text-muted-foreground">{{ currentDecks.length }} decks</span>
        </div>

        <div class="space-y-2">
          <TransitionGroup name="deck">
            <div
              v-for="deck in currentDecks"
              :key="deck.id"
              class="px-4 py-3 rounded-md border transition-all duration-300"
              :class="
                isUpdating && deck.toDelete
                  ? 'bg-red-100 dark:bg-red-900/30 border-red-400'
                  : isUpdating && deck.toRename
                    ? 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-400'
                    : 'bg-muted/50 border-border'
              "
            >
              <div class="flex items-center justify-between">
                <p class="text-sm font-medium text-foreground">{{ deck.name }}</p>
                <span class="text-xs text-muted-foreground">{{ deck.cardCount }} cards</span>
              </div>
            </div>
          </TransitionGroup>
        </div>
      </div>
      </div>
    </div>
  </div>
  </Transition>
</template>

<style scoped>
/* Message fade-in (skip for typed messages) */
.message-enter-active:not(.skip-transition) {
  transition: opacity 0.3s ease;
}
.message-enter-from:not(.skip-transition) {
  opacity: 0;
}

/* Deck transitions */
.deck-leave-active {
  transition: all 0.4s ease-out;
}
.deck-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Demo fade in/out */
.demo-enter-active,
.demo-leave-active {
  transition: opacity 0.4s ease;
}
.demo-enter-from,
.demo-leave-to {
  opacity: 0;
}
</style>
